const { isEmpty } = require("lodash");

const grpcHandler = require("./optimizely.grpc");
const httpHandler = require("./optimizely.http");

let handler;

/**
 * Use to initialize a connection to optimizely fullstack
 * @param {{ optimizelyHost: string, grpcInstance: object, useHttp: boolean }} config Provide initialization configuration
 */
function start(config) {
  if (isEmpty(config)) {
    throw new Error("Required initialization data not provided: config");
  }

  const { optimizelyHost, grpcInstance, useHttp } = config;
  if (useHttp) {
    handler = httpHandler(optimizelyHost);
  } else {
    handler = grpcHandler(optimizelyHost, grpcInstance);
  }
}

/**
 * Asks optimizely if a feature TOGGLE is enabled for a user given certain attrbutes. This
 * will return true if the feature toggle is on for the user in the audience and traffic allocation.
 *
 * It sends an impression event if any experiments are on and the user qualifies for them.
 *
 * https://docs.developers.optimizely.com/full-stack/docs/is-feature-enabled-javascript-node
 *
 * It first checks any attached experiments where the audience and traffic allocation match, and if
 * neither traffic nor audience critera match for any enabled experiments, then move on to the
 * actual feature settings, where it also evaluates audience and traffic settings to determine if a
 * feature is enabled.
 *
 * @param {string} featureKey The name of the optimizely feature we want to check
 * @param {object} context The user id context
 * @param {object} attributes Any attributes we need to match on
 * @returns {Promise<{enabled: boolean, variationKey: string, variables: Object.<string, boolean|number|string>?}>} Optimizely feature enables, variations, and variables.
 */
function isFeatureEnabled(featureKey, context, attributes) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  return handler.isFeatureEnabled(featureKey, context, attributes);
}

/**
 * Asks optimizely first for a list of all known features. Then, we iterate over that list and ask optimizely
 * if the feature toggle is on for the given user in the audience and traffic allocation.
 *
 * It will NOT send an impressions event for the user.
 *
 * https://docs.developers.optimizely.com/full-stack/docs/get-enabled-features-javascript-node
 *
 * Similarly to isFeatureEnabled, it first checks audience match and traffic allocation match on attached experiments
 * and then moves on to the feature itself,
 *
 * @param {object} context The user id context
 * @param {object} attributes The attributes we need to match on
 * @param {Array<string>} selectedFeatureKeys The subset of keys we care about
 *
 * @returns {Promise<Object.<string, boolean>>} Dictionary  of optimizely features and their enabled status
 */
function getEnabledFeatures(context, attributes, selectedFeatureKeys) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  return handler.getEnabledFeatures(context, attributes, selectedFeatureKeys);
}

/**
 * Activates an A/B test for the specified user to start an experiment: determines whether they qualify
 * for the experiment, buckets a qualified user into a variation, and sends an impression event to Optimizely.
 *
 * https://docs.developers.optimizely.com/full-stack/docs/activate-javascript-node
 *
 * Note: This function is only used for A/B tests as set up in optimizely. Feature tests (default) use
 * isFeatureEnabled instead and attribution will be odd if you use this instead.
 *
 * @param {string} experimentKey The name of the A/B test from optimizely
 * @param {object} context The user id context
 * @param {object} attributes Any attributes we use to match an audience from
 * @returns {Promise<{enabled: boolean, variationKey: string, variables: Object.<string, boolean|number|string>?}>}
 */
function activate(experimentKey, context, attributes) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  return handler.activate(experimentKey, context, attributes);
}

/**
 * Returns a variation where the visitor will be bucketed, without triggering an impression.
 *
 * https://docs.developers.optimizely.com/full-stack/docs/get-variation-javascript-node
 *
 *
 * @param {string} experimentKey The name of the A/B test from optimizely
 * @param {object} context The user id context
 * @param {object} attributes The attributes we need to match on
 * @returns {Promise<{variationKey: string, variables: Object.<string, boolean|number|string>?}>}
 */
function getVariation(experimentKey, context, attributes) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  return handler.getVariation(experimentKey, context, attributes);
}

/**
 * Forces a user into a specific variation. this is an IN MEMORY mutation that can be used for QA'ing in
 * different environments if necessary.
 *
 * https://library.optimizely.com/docs/api/agent/v1/index.html#operation/override
 *
 * @param {string} experimentKey The name of the A/B test from optimizely
 * @param {object} context The attributes we need to match on
 * @param {string} variationKey The variation to explicitly assign
 * @returns {Promise<{newVariationKey: string, oldVariationKey: string, messages: Array<String>}>}
 */
function setForcedVariation(experimentKey, context, variationKey) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  if (!handler.setForcedVariation) {
    throw new Error("Only supported via gRPC");
  }

  return handler.setForcedVariation(experimentKey, context, variationKey);
}

/** @typedef {import("@greenlight/protos/gl/optimizely/v1beta1/optimizely_api_pb").TrackResponse.AsObject} TrackResponseObject */
/**
 * Send event and user details to Optimizely analytics backend, so you can see metrics for an experiment. You can view
 * metrics either on your Results page or as a data export.
 *
 * https://library.optimizely.com/docs/api/agent/v1/index.html#operation/trackEvent
 *
 * @param {string} eventKey The name of the Event to send
 * @param {object} context The user id context
 * @param {object} attributes The attributes we need to match on
 * @param {object} eventTags The variation to explicitly assign
 * @returns {TrackResponseObject}
 */
function track(eventKey, context, attributes, eventTags) {
  if (!handler) {
    throw new Error("Need to initialize the optimizely handler");
  }

  return handler.track(eventKey, context, attributes, eventTags);
}

module.exports = {
  start,
  isFeatureEnabled,
  getEnabledFeatures,
  activate,
  getVariation,
  setForcedVariation,
  track
};
